import { useEffect, useRef, useState } from 'react'
import classnames from 'classnames'

import { type Breadcrumb } from './types'
import styles from './FileBrowser.module.scss'

const getVisibleIndices = (breadcrumbs: Breadcrumb[], maxVisible: number): number[] => {
  if (maxVisible >= breadcrumbs.length) {
    return breadcrumbs.map((_, i) => i)
  }
  const tailCount = Math.max(maxVisible - 1, 1)
  const tailStart = breadcrumbs.length - tailCount
  return [0, ...Array.from({ length: tailCount }, (_, i) => tailStart + i)]
}

interface BreadcrumbBarProps {
  breadcrumbs: Breadcrumb[]
  onNavigate: (index: number) => void
}

export const BreadcrumbBar = ({ breadcrumbs, onNavigate }: BreadcrumbBarProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef<number | null>(null)
  const [maxVisible, setMaxVisible] = useState<number>(breadcrumbs.length)
  const [prevLength, setPrevLength] = useState<number>(breadcrumbs.length)

  if (prevLength !== breadcrumbs.length) {
    setPrevLength(breadcrumbs.length)
    setMaxVisible(breadcrumbs.length)
  }

  useEffect(() => {
    const containerElement = containerRef.current
    if (!containerElement) {
      return
    }

    const collapseIfNeeded = (currentMax: number) => {
      frameRef.current = null
      if (containerElement.scrollWidth > containerElement.clientWidth && currentMax > 2) {
        const nextMax = currentMax - 1
        setMaxVisible(nextMax)
        frameRef.current = requestAnimationFrame(() => collapseIfNeeded(nextMax))
      }
    }

    const scheduleCollapse = () => {
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current)
      }
      frameRef.current = requestAnimationFrame(() => collapseIfNeeded(breadcrumbs.length))
    }

    scheduleCollapse()

    const observer = new ResizeObserver(() => {
      setMaxVisible(breadcrumbs.length)
      scheduleCollapse()
    })
    observer.observe(containerElement)

    return () => {
      observer.disconnect()
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current)
      }
    }
  }, [breadcrumbs.length])

  const visibleIndices = getVisibleIndices(breadcrumbs, maxVisible)
  const collapsed = maxVisible < breadcrumbs.length

  return (
    <div ref={containerRef} className={classnames(styles.breadcrumbBar, 'paragraph-small')}>
      {visibleIndices.map((index, pos) => {
        const crumb = breadcrumbs[index]
        const isLast = index === breadcrumbs.length - 1
        const isRoot = index === 0
        const showEllipsis = collapsed && pos === 1

        return (
          <span key={crumb.id} className='d-flex align-items-center gap-1'>
            {pos > 0 && !showEllipsis ? <i className={classnames('bi bi-chevron-right', styles.breadcrumbSeparator)} /> : null}
            {showEllipsis ? (
              <>
                <i className={classnames('bi bi-chevron-right', styles.breadcrumbSeparator)} />
                <span className={styles.breadcrumbEllipsis}>…</span>
                <i className={classnames('bi bi-chevron-right', styles.breadcrumbSeparator)} />
              </>
            ) : null}
            {isLast ? (
              <span className={styles.breadcrumbCurrent}>{isRoot ? <i className='bi bi-house-door-fill' /> : crumb.name}</span>
            ) : (
              <button className={styles.breadcrumbLink} onClick={() => onNavigate(index)}>
                {isRoot ? <i className='bi bi-house-door-fill' /> : crumb.name}
              </button>
            )}
          </span>
        )
      })}
    </div>
  )
}
